/**
 * Org-level gates for platform "Criar instância" and Valut-only Configuração tabs.
 *
 * Does NOT use IB hasPermission("admin") — that is often defaulted for Carteira access.
 * Resolves BaasApiInstanceUsers role/permissions via my-permissions on the AllowGlobalAccess
 * (or create-parent) instance.
 *
 * - canCreateInstance: super_user | Valut matrix create | client admin + CanCreateChildInstances
 * - isValutConfigAdmin: super_user | Valut matrix (features/limits/fees/scopes writes)
 * Never grant clients AllowGlobalAccess / Valut matrix membership via this hook.
 */

import { useEffect, useMemo, useState } from "react";
import { useBaasInstance } from "../Contexts/BaasInstanceContext";
import {
  evaluateCapability,
  isBaasConsoleReadOnly,
  isBaasMasterRole,
} from "../Pages/Private/BaasConsole/baasCapabilities";
import { readBaasViewAsPersona } from "../Pages/Private/BaasConsole/baasViewAs";
import { mobileConsoleRequest, resolveBaasProxyContext } from "../Services/baasConsoleClient";
import { resolveBaasCreateParentInstance } from "../Services/baasInstancesApi";

const parseGrant = (data) => {
  const role = String(data?.role ?? data?.Role ?? "")
    .trim()
    .toLowerCase();
  const caps = data?.capability_permissions ?? data?.capabilityPermissions;
  const permissions =
    Array.isArray(caps) && caps.length > 0
      ? caps.map(String)
      : Array.isArray(data?.permissions)
        ? data.permissions.map(String)
        : [];
  const canCreateChildInstances = Boolean(
    data?.can_create_child_instances ?? data?.canCreateChildInstances,
  );
  const isValutPlatformAdmin = Boolean(
    data?.is_valut_platform_admin ?? data?.isValutPlatformAdmin,
  );
  return {
    permissions,
    role: role || null,
    canCreateChildInstances,
    isValutPlatformAdmin,
  };
};

export const useBaasPlatformCreateAccess = () => {
  const { instances, loading: instancesLoading } = useBaasInstance();

  const permissionInstance = useMemo(() => {
    const list = Array.isArray(instances) ? instances : [];
    const matrix = list.find(
      (row) => row?.allowGlobalAccess === true && Number(row?.baasInstanceId) > 0,
    );
    if (matrix) return matrix;
    return resolveBaasCreateParentInstance(list, "production");
  }, [instances]);

  const hasMatrixAccess = useMemo(
    () =>
      (Array.isArray(instances) ? instances : []).some(
        (row) => row?.allowGlobalAccess === true,
      ),
    [instances],
  );

  const [grant, setGrant] = useState({
    permissions: [],
    role: null,
    canCreateChildInstances: false,
    isValutPlatformAdmin: false,
  });
  const [grantLoading, setGrantLoading] = useState(true);
  const [viewAsTick, setViewAsTick] = useState(0);

  useEffect(() => {
    const onViewAs = () => setViewAsTick((n) => n + 1);
    window.addEventListener("baas-view-as-changed", onViewAs);
    window.addEventListener("storage", onViewAs);
    return () => {
      window.removeEventListener("baas-view-as-changed", onViewAs);
      window.removeEventListener("storage", onViewAs);
    };
  }, []);

  const viewAsActive = useMemo(() => Boolean(readBaasViewAsPersona()), [viewAsTick]);

  useEffect(() => {
    let cancelled = false;

    if (!permissionInstance) {
      setGrant({
        permissions: [],
        role: null,
        canCreateChildInstances: false,
        isValutPlatformAdmin: false,
      });
      setGrantLoading(Boolean(instancesLoading));
      return undefined;
    }

    setGrantLoading(true);
    (async () => {
      try {
        const ctx = resolveBaasProxyContext(permissionInstance);
        const data = await mobileConsoleRequest({
          method: "GET",
          path: `v1/ib/baas/instances/${ctx.instanceId}/my-permissions`,
        });
        if (!cancelled) {
          setGrant(parseGrant(data));
        }
      } catch {
        // Fail closed — never treat unknown / 403 as platform create.
        if (!cancelled) {
          setGrant({
            permissions: [],
            role: "viewer",
            canCreateChildInstances: false,
            isValutPlatformAdmin: false,
          });
        }
      } finally {
        if (!cancelled) {
          setGrantLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [permissionInstance, instancesLoading]);

  const capabilities = useMemo(
    () => ({
      products: {},
      permissions: grant.permissions,
      role: grant.role,
    }),
    [grant],
  );

  const isMaster = isBaasMasterRole(grant.role);

  const isReadOnly = useMemo(
    () => isBaasConsoleReadOnly(capabilities),
    [capabilities],
  );

  const canCreateByCapability = useMemo(
    () => evaluateCapability(capabilities, "instances.create").allowed,
    [capabilities],
  );

  const loading = Boolean(instancesLoading || grantLoading);

  /**
   * Real Valut platform admin (matrix / super_user) — ignores "Visualizar como".
   * Prefer BE is_valut_platform_admin; fall back to matrix membership with
   * console.write / instances.create only — NEVER "has matrix + not viewer"
   * (Operador has transactions.write so isReadOnly=false and was wrongly elevated).
   */
  const isValutConfigAdminRaw = Boolean(
    !loading &&
      (isMaster ||
        grant.isValutPlatformAdmin ||
        (hasMatrixAccess && canCreateByCapability)),
  );

  /**
   * Effective Valut admin for UI gates. Suppressed while "Visualizar como" is active so
   * owner / liberate / Valut-only settings match the impersonated client persona.
   */
  const isValutConfigAdmin = isValutConfigAdminRaw && !viewAsActive;

  /**
   * Criar instância:
   * - Master / Valut matrix create, or
   * - Client admin with CanCreateChildInstances on the owner entity.
   * Suppressed during view-as (same as partner would see).
   */
  const canCreateInstance = Boolean(
    !loading &&
      !viewAsActive &&
      (isMaster ||
        isValutConfigAdminRaw ||
        (!isReadOnly && canCreateByCapability && grant.canCreateChildInstances)),
  );

  return {
    isReadOnly,
    canCreateInstance,
    /** Valut-only settings tabs (scopes/features/limits/fees). Suppressed in view-as. */
    isValutConfigAdmin,
    /**
     * Real matrix admin — keep View As picker/banner available while previewing.
     */
    canUseViewAs: isValutConfigAdminRaw,
    canCreateChildInstances: Boolean(grant.canCreateChildInstances),
    loading,
    capabilities,
    isMaster: isMaster && !viewAsActive,
    viewAsActive,
  };
};
